import { getBlocksData } from '@zillow/cms-blocks';

import type { ResolvedBanner } from '../types/shared';

/**
 * Processes blocks data from a container (footer, sidebar, etc.)
 * @param container - The container object that has a blocks property
 * @returns Processed blocks data array, or empty array if processing fails
 */
export const processBlocksData = async (
    container: { blocks?: string } | null
) => {
    if (!container) return [];
    try {
        return await getBlocksData(JSON.parse(container.blocks || '[]'));
    } catch (error) {
        console.error('Error processing blocks:', error);
        return [];
    }
};

/**
 * Processes the banner resolved for a request into the shape BannerTemplate
 * renders. Unlike sidebars and footers, the id and meta are carried through:
 * the id keys the per-banner dismissal (MOPS-29796) and the meta holds the
 * bar's close control setting.
 *
 * Returns null when no banner is assigned or the payload has no id/blocks.
 * This does not tolerate a missing schema field: post/term queries select
 * `banner`, so GraphQL validation fails unless cms-graph already exposes it.
 *
 * @param banner - The resolved banner container from cms-graph
 * @returns `{ id, blocks, meta }`, or null when there is nothing to render
 */
export const processResolvedBanner = async (
    banner?: {
        id?: string;
        blocks?: string;
        meta?: { dismissible?: boolean | null };
    } | null
): Promise<ResolvedBanner | null> => {
    if (!banner?.id) return null;
    const blocks = await processBlocksData(banner);
    if (blocks.length === 0) return null;

    return {
        id: banner.id,
        blocks,
        meta: {
            dismissible: Boolean(banner.meta?.dismissible),
        },
    };
};
